import type { ComponentType } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  Building2,
  Code2,
  CreditCard,
  Mail,
  MessageCircle,
  ScrollText,
  ShieldCheck,
  Sparkles,
  UserRound,
  UsersRound,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/app/auth/useAuth';
import type { AbilityCheck } from '@/app/auth/ability';

const SECTIONS: {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  can?: AbilityCheck;
}[] = [
  { to: 'profile', label: 'My profile', icon: UserRound },
  { to: 'workspace', label: 'Workspace', icon: Building2, can: ['read', 'Workspace'] },
  { to: 'billing', label: 'Billing & plan', icon: CreditCard, can: ['read', 'Billing'] },
  { to: 'agents', label: 'Agents & roles', icon: ShieldCheck, can: ['read', 'User'] },
  { to: 'teams', label: 'Teams', icon: UsersRound, can: ['read', 'Team'] },
  { to: 'ai', label: 'AI assistant', icon: Sparkles, can: ['manage', 'AiSettings'] },
  { to: 'widget', label: 'Chat widget', icon: MessageCircle, can: ['manage', 'Channel'] },
  { to: 'email', label: 'Email channel', icon: Mail, can: ['manage', 'Channel'] },
  { to: 'api', label: 'API & MCP', icon: Code2, can: ['manage', 'ApiKey'] },
  { to: 'audit-log', label: 'Audit log', icon: ScrollText, can: ['read', 'AuditLog'] },
];

export function SettingsLayout() {
  const { can } = useAuth();
  const visible = SECTIONS.filter((s) => !s.can || can(...s.can));
  return (
    <div className="mx-auto max-w-[1400px]">
      <div className="flex flex-col gap-5 md:flex-row md:gap-6">
        <nav
          aria-label="Settings"
          className="-mx-4 flex gap-1 overflow-x-auto px-4 md:sticky md:top-24 md:mx-0 md:w-44 md:shrink-0 md:flex-col md:self-start md:px-0"
        >
          <p className="mb-1 hidden px-2.5 text-[10px] font-semibold tracking-[0.14em] text-slate-400 uppercase md:block">
            Settings
          </p>
          {visible.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] font-medium whitespace-nowrap transition-colors',
                  isActive
                    ? 'text-brand-700 shadow-card bg-white ring-1 ring-slate-200'
                    : 'text-slate-600 hover:bg-white/70 hover:text-slate-900',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={cn(
                      'h-4 w-4 shrink-0',
                      isActive ? 'text-brand-600' : 'text-slate-400 group-hover:text-slate-600',
                    )}
                  />
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
